/**
 * This file has the message formats for the different messages that are
 * received by the client from the websocket.
 *
 * Each websocket `message` contains an `event` that has a:
 *  - `type`,
 *  - `parent_type` and
 *  - `data`.
 *
 * These `events` are then reused by the `store` and `actions`.
 */

import { BudgetMinMaxBackend } from '@shared/datastore/budget-min-max';
import { OnlineOfflineUserStatusValueApi } from '@shared/datastore/online-offline';
import {
  ContextTypeApi,
  PrivacyLevelApi,
  RichMessageApi,
  SourceTypeApi,
  ThreadTypeApi,
} from 'api-typings/messages/messages_types';
import {
  BidAwardStatusApi,
  ExpertGuaranteeApi,
  MilestoneReasonApi,
  MilestoneStatusApi,
} from 'api-typings/projects/projects';

import { CurrencyAjax } from '../../currencies/currencies.model';
import { TimeSeconds } from '../time/time.model';

export type WebsocketServerEvent = SubMessageEvent | WebsocketMessage;

interface SubMessageEvent {
  channel: 'subscribe';
  body: 'OK';
}

// --- Notification events (from event_notify.php and Python services) ---

// This models the data from getContestData() in event_notify.php
export type WebsocketContestData = TimeSeconds & {
  id: string | number;
  actionText: any;
  appended_descr: string;
  buyer: string;
  buyerUrl: string;
  imgUrl: string;
  isTest: boolean;
  jobs: number[] | string[];
  jobString: string;
  linkUrl: string;
  title: string;
  userId: string;
  userName: string;
};

// This models the data from pack_project() in event_notify.php
export type WebsocketProjectData = TimeSeconds & {
  id: number;
  actionText: string;
  appended_descr: string;
  imgUrl: string;
  isMinimallyPostedProject: boolean;
  isTest: boolean;
  jobs: string[] | null;
  jobString: string;
  linkUrl: string;
  minimallyPostedProjectBudgetSet: boolean; // TODO: T41420
  recruiter: boolean;
  showBudgetMinimallyPostedProject: boolean;
  text: string;
  title: string;
  userId: string | number;
  userName: string;
};

type WebsocketServiceData = TimeSeconds & {
  id: number;
  service_id: string | number;
  service_name: string;
  service_url: string;
  service_duration: string;
  service_cost: string | number;
  currency_sign: string;
  currency_code: string;
  service_img: string; // A URL
};

interface BaseServerData {
  data: TimeSeconds;
  id: string;
}

export interface WebsocketArticleCommentEvent extends BaseServerData {
  type: 'articleCommentReceived';
  parent_type: 'notifications';
  data: TimeSeconds & {
    article_author: string | number; // The ID of author
    article_name: string;
    article_type: string; // Some enum?
    comment_by: string | number;
    comment_date:
      | string
      | { timezone_type: number; date: string; timezone: string };
    comment_id: string | number;
    comment: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    publicName: string;
    user: string;
  };
}

export interface WebsocketAwardEvent extends BaseServerData {
  type: 'award';
  parent_type: 'notifications';
  data: TimeSeconds & {
    acceptByTime?: number;
    actionText?: string;
    apiMessage: {
      bid: {
        amount: number;
        award_status: BidAwardStatusApi.PENDING;
        bidder_id: number;
        id: number;
        period: number;
      };
      project: {
        currency: { code: string; sign: string };
        id: number;
        owner_id: number;
        title: string;
        type: 'fixed' | 'hourly';
      };
      users: { [id: string]: { username: string } };
    };
    appended_descr?: string;
    awardeeId?: number;
    awardeeName?: string;
    bid?: {
      bidAmount: string;
      bidDescr: string | null;
      bidId: number;
      bidPeriod: string;
      id: number;
    };
    categoryName?: string;
    currencycode?: string;
    currencysign?: string;
    id: number;
    imgUrl?: string;
    isTest?: boolean;
    jobs: string[];
    jobString: string;
    linkUrl?: string;
    projIsHourly?: boolean;
    text?: string;
    title: string;
    userId: number;
    userName: string;
  };
}

export interface WebsocketAwardBadgeEvent extends BaseServerData {
  type: 'awardBadge';
  parent_type: 'notifications';
  data: TimeSeconds & {
    descr: string;
    id: number;
    linkUrl: string;
    name: string;
  };
}

export interface WebsocketAwardCreditEvent extends BaseServerData {
  type: 'awardCredit';
  parent_type: 'notifications';
  data: TimeSeconds & {
    amount: string | number;
    id: string | number;
    linkUrl: string;
  };
}

export interface WebsocketAwardReminderEvent extends BaseServerData {
  type: 'awardReminder';
  parent_type: 'notifications';
  data: TimeSeconds & {
    // Note, this doesn't actually have an `id`.
    imgUrl: string;
    linkUrl: string;
    name: string;
    userName: string;
    bid: { id: string | number };
    // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
  };
}

export interface WebsocketAwardMilestoneReminderEvent extends BaseServerData {
  type: 'awardMilestoneReminder';
  parent_type: 'notifications';
  data: TimeSeconds & {
    appended_descr: string;
    currencycode: string;
    currencyid: string | number;
    currencysign: string;
    id: string | number;
    imgUrl: string;
    // isAutomaticPayments: boolean;
    linkUrl: string;
    // projIsHourly: boolean;
    publicName?: string;
    title: string;
    userId: string | number;
    userName: string;
    // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
  };
}

export interface WebsocketAwardXpEvent extends BaseServerData {
  type: 'awardXp';
  parent_type: 'notifications';
  data: TimeSeconds & {
    amount: string | number;
    descr: string;
    game: 'xp';
    id: string | number;
    linkUrl: string;
  };
}

interface WebsocketBidEventProjectCount {
  all: number;
  incomplete_reviews: number;
  complete: number;
  completion_rate: number;
  reviews: number;
  earnings: number;
  incomplete: number;
}

interface WebsocketBidEventReputation {
  com: number;
  exp: number;
  hag: number;
  onb: number;
  ont: number;
  overall: number;
  pos: number;
  pro: number;
  qua: number;
}

interface WebsocketBidEventSellerRating {
  count: number;
  projectCount: {
    last12months: WebsocketBidEventProjectCount;
    last3months: WebsocketBidEventProjectCount;
    entire_history: WebsocketBidEventProjectCount;
  };
  completionRate: number;
  avg: number;
  reputation: {
    last12months: WebsocketBidEventReputation;
    last3months: WebsocketBidEventReputation;
    entire_history: WebsocketBidEventReputation;
  };
}

export interface WebsocketBidretractedEvent extends BaseServerData {
  type: 'bidretracted';
  parent_type: 'notifications';
  data: TimeSeconds & {
    bid_id: number;
    project_id: number;
    seller_id: number;
  };
}

export interface WebsocketBidEvent extends BaseServerData {
  type: 'bid' | 'bid_updated' | 'bid_self';
  parent_type: 'notifications';
  data: TimeSeconds & {
    accountId?: number;
    amount: string;
    award?: string;
    bidAvg?: string | number | false;
    bidCount?: number;
    bidHidden: boolean;
    bidNo?: string | false;
    buyerFeePerDollar?: string;
    buyerMinProjectFee?: string;
    categoryName?: string;
    currency: string;
    currencyCode: string;
    currencyId: string;
    descr: string;
    id: string | number;
    imgUrl: string;
    initMilestone?: number;
    linkUrl?: string;
    period: string;
    pmb: any;
    projectId: string | number;
    projIsFullTime: boolean;
    projIsHourly: boolean;
    projName: string;
    publicName?: string;
    showAwardFee: boolean;
    text?: string;
    title: string;
    userId: string | number;
    userName: string;
    userBalances?: {
      all:
        | {
            currency: {
              code: string;
              country: string;
              exchangerate: string;
              id: string;
              is_external?: string;
              name: string;
              seq: string;
              sign: string;
            };
            balance: string;
          }[]
        | null;
      relevant: number;
    } | null;
    bid: {
      award_score?: number;
      completed?: boolean;
      completion_score?: number;
      descr_html?: string;
      descr_short_html?: string;
      descr_short?: string;
      descr?: string | null;
      expert_guarantee?: ExpertGuaranteeApi | null;
      highlighted?: boolean | null;
      id: string | number;
      isShortlisted?: boolean | null;
      milestone_percentage?: string | number;
      new_ranking?: number | boolean;
      no?: string | false;
      paid_status?: false | null;
      period?: string;
      project_id?: string | number;
      r2?: number;
      ranking?: number;
      score?: number;
      sponsored?: boolean | null;
      submitdate_f?: string;
      submitdate_f2?: string;
      submitdate_ts?: number;
      submitdate?: string;
      sum?: string;
      users_id?: number;
      user?: {
        address: { country: string | null; code: string; city: string | null };
        company: string | null;
        currency: number;
        earnings: string | number;
        flag_class: string;
        flag_name: string | null;
        flag: { name: string | null; icon: string };
        gold: number;
        hourlyrate: number | null;
        id: number;
        insignia: {
          name: string;
          title: string;
          examEnabled: string | null;
          examId: string | null;
          id: string;
          description: string;
        }[];
        isPortfolioCreated: boolean;
        jobs: string[];
        logo_Url?: string;
        normalMemberclassLogo: string;
        online?: string | null;
        profile_logo_Url?: string;
        provider_rating: { count: number; avg: string | number };
        publicName?: string;
        rating: { count: number; avg: string | number };
        reg_date: string;
        reputation_rate: number;
        timezone: number;
        tooltipMemberclassLogo: string;
        Url?: string;
        username: string;
        seller_rating: WebsocketBidEventSellerRating;
      };
    };
  };
}

interface BidWebsocketApiMessage {
  bid: {
    amount?: number;
    award_status: BidAwardStatusApi;
    bidder_id: number;
    id: number;
    period?: number;
  };
  project: {
    currency?: {
      code: string;
      id: number;
      sign: string;
    };
    id: number;
    owner_id: number;
    title?: string;
  };
  users?: {
    [index: string]: { username: string };
  };
}

export interface WebsocketAcceptedEvent {
  parent_type: 'notifications';
  type: 'accepted';
  data: TimeSeconds & {
    bid: {
      retracted: boolean;
      descr: string | null;
    };
    apiMessage: BidWebsocketApiMessage;
  };
}

export interface WebsocketRevokedEvent {
  parent_type: 'notifications';
  type: 'revoked';
  data: TimeSeconds & {
    apiMessage: BidWebsocketApiMessage;
    awardeeId: number;
    bidId: string | number;
    projectId: number;
  };
}

export interface WebsocketCancelMilestoneEvent {
  type: 'cancelMilestone';
  parent_type: 'notifications';
  data: TimeSeconds & {
    id: number;
    tranId: string | number;
  };
}

export interface WebsocketCompletedEvent extends BaseServerData {
  type: 'completed';
  parent_type: 'notifications';
  data: TimeSeconds & {
    amount: string | number;
    categoryName: string;
    completeStatus: string; // TODO: T37445 Make this a string enum
    currencycode: string;
    currencyid: string | number;
    currencysign: string;
    employerId: string | number;
    employerPublicName?: string;
    employerUsername: string;
    expiredTimestamp: string;
    freelancerId: string | number;
    freelancerPublicName?: string;
    freelancerUsername: string;
    id: string | number;
    img_e: { profile_logo_url: string }; // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
    img_f: { profile_logo_url: string }; // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
    name: string;
    // projIsHourly: boolean;
    selectionId: string | number;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    submitDate: string;
  };
}

export interface WebsocketContestEvent extends BaseServerData {
  type: 'contest';
  parent_type: 'notifications';
  data: WebsocketContestData & {
    currency?: string;
    currencyCode: string;
    exchangerate: string;
    extended: boolean;
    featured: boolean;
    free_bid_until?: number;
    highlight: any;
    maxbudget?: string | number;
    private: boolean;
    sealed: boolean;
    submitDate: string;
  };
}

export interface WebsocketContestAwardedToEmployerEvent extends BaseServerData {
  type: 'contestAwardedToEmployer';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contestName: string;
    contestSEOUrl?: string;
    currencyCode: string;
    currencySign: string;
    entry_id: string;
    entry_number: string;
    entryCount: string;
    freelancerCount: string;
    id: string | number;
    imgUrl: string;
    isBought: boolean;
    linkUrl: string;
    prize: string | number;
    publicName?: string;
    thumb: string;
    userId: number;
    userName: string;
  };
}

export interface WebsocketContestAwardedToFreelancerEvent
  extends BaseServerData {
  type: 'contestAwardedToFreelancer';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contestName: string;
    contestSEOURL: string;
    currencyCode: string;
    currencySign: string;
    entry_id: string | number;
    entry_number: string | number;
    entryCount: string | number;
    freelancerCount: string | number;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    prize: string | number;
    publicName?: string;
    userId: string | number;
    userName: string;
    // thumb: ???;
  };
}

export interface WebsocketContestEntryBoughtToEmployerEvent
  extends BaseServerData {
  type: 'contestEntryBoughtToEmployer';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contestName: string;
    entry_id: string | number;
    entry_number: string | number;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    publicName?: string;
    userName: string;
  };
}

export interface WebsocketContestEntryBoughtToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryBoughtToFreelancer';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contestName: string;
    entry_id: string | number;
    entry_number: string | number;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    publicName?: string;
    userName: string;
  };
}

export interface WebsocketContestEntryEvent extends BaseServerData {
  type: 'contestEntry';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contestHolder: string;
    contestId: string;
    contestName: string;
    firstUser?: string;
    firstUserPublicName?: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string | false;
    secondUserPublicName?: string | false;
    userId: string | number;
  };
}

export interface WebsocketContestEntryRatedToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryRatedToFreelancer';
  parent_type: 'notifications';
  data: TimeSeconds & {
    action: string;
    contestId: string;
    contestName: string;
    employerId: string;
    entriesUrl: string;
    entryId: string;
    entryNumber: string;
    entryS3path: string;
    entryThumb: string;
    entryUrl: string;
    freelancerId: string;
    hasOtherEntriesRatedOrRejected: boolean;
    imgUrl: string;
    linkUrl: string;
    ratedOrRejectedEntriesCount: number;
    rating: string;
  };
}

export interface WebsocketContestEntryRejectedToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryRejectedToFreelancer';
  parent_type: 'notifications';
  data: TimeSeconds & {
    action: string;
    contestId: string;
    contestName: string;
    employerId: string;
    entriesUrl: string;
    entryId: string;
    entryNumber: string;
    entryS3path: string;
    entryThumb: string;
    entryUrl: string;
    freelancerId: string;
    hasOtherEntriesRatedOrRejected: boolean;
    imgUrl: string;
    linkUrl: string;
    ratedOrRejectedEntriesCount: number;
    rating: string;
  };
}

export interface WebsocketContestEntryReconsideredToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryReconsideredToFreelancer';
  parent_type: 'notifications';
  data: TimeSeconds & {
    action: string;
    contestId: string;
    contestName: string;
    employerId: string;
    entriesUrl: string;
    entryId: string;
    entryNumber: string;
    entryS3path: string;
    entryThumb: string;
    entryUrl: string;
    freelancerId: string;
    hasOtherEntriesRatedOrRejected: boolean;
    imgUrl: string;
    linkUrl: string;
    ratedOrRejectedEntriesCount: number;
    rating: string;
  };
}

export interface WebsocketContestEntryCommentedToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryCommentedToFreelancer';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contestId: string;
    contestName: string;
    entryNumber: string;
    id: string;
    imgUrl: string;
    linkUrl: string;
  };
}

export interface WebsocketContestExpiredEvent extends BaseServerData {
  type: 'contestExpired';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contest_id: string | number;
    // ???
  };
}

export interface WebsocketContestPCBNotificationEvent extends BaseServerData {
  type: 'contestPCBNotification';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contestHolder: string;
    contestId: string;
    contestName: string;
    firstUser?: string;
    firstUserPublicName?: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string | false;
    secondUserPublicName?: string | false;
    userId: number;
  };
}

export interface WebsocketContestPCBNotificationFullViewEvent
  extends BaseServerData {
  type: 'contestPCBNotificationFullView';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contestHolder: string | number;
    contestName: string;
    entryNumber: string | number | null;
    firstUser: string;
    firstUserPublicName?: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string | false;
    secondUserPublicName?: string | false;
    userId: string | number;
  };
}

export interface WebsocketMilestone {
  readonly transaction_id?: string;
  readonly project_owner_id?: string;
  readonly bidder_id?: string;
  readonly amount?: string;
  readonly reason?: MilestoneReasonApi;
  readonly other_reason?: string;
  readonly project_id?: string;
  readonly bid_id?: string;
  readonly currency?: CurrencyAjax;
  readonly is_from_prepaid?: boolean;
  readonly status?: MilestoneStatusApi;
  readonly dispute_id?: string;
  readonly time_created?: string;
}

export interface WebsocketCreateMilestoneEvent extends BaseServerData {
  type: 'createMilestone';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    account_id: string | number | null;
    amount: string | number;
    bidId: string | number;
    currencycode: string;
    currencysign: string;
    project_id: string | number;
    publicName?: string;
    shortDescr: string;
    transId: string | number;
    /* From NewEscrowHandler::save */
    newMilestone: WebsocketMilestone;
  };
}

export interface WebsocketDenyedEvent extends BaseServerData {
  type: 'denyed';
  parent_type: 'notifications';
  data: TimeSeconds & {
    apiMessage: {
      project: {
        id: number;
        title: string;
        owner_id: number;
        currency: {
          sign: string;
          code: string;
        };
      };
      bid: {
        bidder_id: number;
        award_status: 'rejected';
      };
      users: {
        [userId: string]: { username: string };
      };
    };
    autoRepostProjectId?: string | number;
    autoRepostProjectName?: string;
    bidId: string | number;
    currencycode: string;
    currencyId: string | number;
    currencysign: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    jobsURL: string;
    linkUrl: string;
    name: string;
    projectType: string; // TODO: T37445 Make this a string enum
    projIsHourly: any;
    publicName?: string;
    reason: string;
    seoUrl: string;
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketDraftContestEvent extends BaseServerData {
  type: 'draftContest';
  parent_type: 'notifications';
  data: TimeSeconds & {
    contestEditUrl?: string;
    contestId: string;
    contestName: string;
    contestUrl: string;
    imgUrl: string;
    isPublished: boolean;
  };
}

export interface WebsocketFailingProjectEvent extends BaseServerData {
  type: 'failingProject';
  parent_type: 'notifications';
  data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      address?: string;
      currency?: string;
      currencyCode: string;
      featured: boolean;
      free_bid_until?: number;
      fulltime: boolean;
      hideBids: boolean;
      nonpublic: boolean;
      projectFreeBid: boolean;
      projIsHourly: boolean;
      publicName?: string;
      submitDate: string;
      xpValue: number;
    };
}

export interface WebsocketHighLtvAnnualDiscountEvent extends BaseServerData {
  type: 'highLtvAnnualDiscount';
  parent_type: 'notifications';
  data: TimeSeconds & {
    id: string | number;
    userDisplayName: string;
    linkUrl: string;
  };
}

export interface WebsocketHireMeEvent extends BaseServerData {
  type: 'hireMe';
  parent_type: 'notifications';
  // NOTE: doesn't have isTest, so can't use WebsocketProjectData
  data: TimeSeconds & {
    // acceptByTime: $acceptByTime;
    // accountId: $project->getAccountId();
    appended_descr: string;
    // bid: $myData['bid'];
    currency?: { code: string; sign: string };
    // currencyCode: string;
    // currencyId: string | number;
    // currencySign: string;
    // featured: $project->isFeatured();
    id: string | number;
    // img: $img;
    imgUrl: string;
    jobString: string;
    linkUrl: string;
    // name: string;
    period?: string | number;
    projIsHourly: boolean;
    publicName?: string;
    // seoUrl: $project->getSeoUrl();
    sum?: string | number;
    title: string;
    userId: number;
    userName: string;
  };
}

export interface WebsocketInviteUserBidEvent extends BaseServerData {
  type: 'inviteUserBid';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    currencycode: string;
    currencysign: string;
    inviteeId: string | number;
    minBudget?: number; // The only place with camelCase min/maxBudget
    maxBudget?: number | false; // TODO: use BudgetMinMaxBackend if possible (requires backend changes)
    inviteeName: string;
    // projIsHourly: boolean;
    publicName?: string;
  };
}

export interface WebsocketInviteToContestEvent extends BaseServerData {
  type: 'inviteToContest';
  parent_type: 'notifications';
  data: TimeSeconds & {
    buyer: string | number; // The ID
    buyerName: string;
    buyerPublicName?: string;
    buyerUrl: string;
    contest_recommended_skills: string;
    currencycode: string;
    currencysign: string;
    description: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    name: string;
    prize: string | number;
    sellerId: string | number;
    sellerName: string;
    sellerPublicName?: string;
    title: string;
    type: string | null;
  };
}

export interface WebsocketInvoiceRequestedEvent extends BaseServerData {
  type: 'invoiceRequested';
  parent_type: 'notifications';
  data: TimeSeconds & {
    accountId: string | number;
    amount: string | number;
    bidId: string | number;
    currencyCode: string;
    currencySign: string;
    dueDate: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    // invoiceVars: ???;
    invoiceId: string | number;
    linkUrl: string;
    name: string;
    // projIsHourly: boolean;
    publicName?: string;
    sellerId: string | number;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketLevelUpEvent extends BaseServerData {
  type: 'levelUp';
  parent_type: 'notifications';
  data: TimeSeconds & {
    id: string | number;
    level: string;
    membership: boolean;
    perks: {
      description: string;
      item: boolean;
      magnitude: number;
      name?: 'BidRefreshRate' | 'item';
      symbol: string;
    }[];
    username: string;
    linkUrl: string;
    game: 'level';
  };
}

export interface WebsocketLocalJobPostedEvent extends BaseServerData {
  // Note: this is actually an extension of WebsocketProjectEvent's data.
  type: 'localJobPosted';
  parent_type: 'notifications';
  data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      address?: string | null;
      currency?: string;
      currencyCode: string;
      exchangerate: string;
      featured: boolean;
      free_bid_until?: number;
      fulltime: boolean;
      hideBids: boolean;
      isLocal: true;
      nonpublic: boolean;
      projIsHourly: boolean;
      submitDate: string;
      publicName?: string;
    };
}

// event_notify.php#logout returns an empty object, so no additional fields
// in this one
export interface WebsocketLogoutEvent extends BaseServerData {
  type: 'logout';
  parent_type: 'notifications';
  data: TimeSeconds & {};
}

export interface WebsocketNotifyFollowerEvent extends BaseServerData {
  type: 'notifyfollower';
  parent_type: 'notifications';
  data: WebsocketProjectData;
}

export interface WebsocketProjectEvent extends BaseServerData {
  type: 'project';
  parent_type: 'notifications';
  data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      address?: string;
      currency?: string;
      currencyCode: string;
      exchangerate: string;
      featured: boolean;
      free_bid_until?: number;
      fulltime: boolean;
      hideBids: boolean;
      isLocal: boolean;
      nonpublic: boolean;
      projIsHourly: boolean;
      submitDate: string;
      publicName?: string;
    };
}

export interface WebsocketRecruiterProjectEvent extends BaseServerData {
  type: 'recruiterProject';
  parent_type: 'notifications';
  data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      address?: string;
      currency?: string;
      currencyCode: string;
      exchangerate: string;
      featured: boolean;
      free_bid_until?: number;
      fulltime: boolean;
      hideBids: boolean;
      isLocal: boolean;
      nonpublic: boolean;
      projIsHourly: boolean;
      submitDate: string;
      publicName?: string;
    };
}

export interface WebsocketQuickHireProjectEvent extends BaseServerData {
  type: 'quickHireProject';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    bidderId: string | number;
    bidderName: string;
    currency?: { code: string; sign: string };
    // featured: boolean; // TODO: T37447 Are these boolean or something different?
    // fulltime: boolean; // TODO: T37447 Are these boolean or something different?
    // hideBids: boolean; // TODO: T37447 Are these boolean or something different?
    // nonpublic: boolean; // TODO: T37447 Are these boolean or something different?
    period?: string | number;
    projIsHourly: boolean;
    publicName?: string;
    submitDate: string;
    sum?: string | number;
  };
}

export interface WebsocketReleaseMilestoneEvent extends BaseServerData {
  type: 'releaseMilestone';
  parent_type: 'notifications';
  data: TimeSeconds & {
    accountId: string | number;
    amount: string | number;
    currencycode: string;
    currencysign: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    linkUrl: string;
    name: string;
    otherReason: string;
    publicName?: string;
    seoUrl: string;
    submitDate: string;
    title: string;
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketRequestEndProjectEvent extends BaseServerData {
  type: 'requestEndProject';
  parent_type: 'notifications';
  data: TimeSeconds & {
    accountId: string | number;
    bidId: string | number;
    categoryName: string;
    dayCount: string | number;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    seoUrl: string;
    submitDate: string;
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketRequestMilestoneEvent extends BaseServerData {
  type: 'requestMilestone';
  parent_type: 'notifications';
  data: TimeSeconds & {
    accountId: string | number;
    amount: string | number;
    bidId: string | number;
    currencyCode: string;
    currencyId: string | number;
    currencySign: string;
    description: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    requestId: string | number;
    sellerId: string | number;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    submitDate: string;
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketRequestToReleaseEvent extends BaseServerData {
  type: 'requestToRelease';
  parent_type: 'notifications';
  data: TimeSeconds & {
    accountId: string | number;
    amount: string | number;
    bidId: string | number;
    currencycode: string;
    currencysign: string;
    descr: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    tranId: string | number;
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketServiceApprovedEvent extends BaseServerData {
  type: 'serviceApproved';
  parent_type: 'notifications';
  data: WebsocketServiceData;
}

export interface WebsocketServiceProjectEndingSellerEvent
  extends BaseServerData {
  type: 'serviceProjectEndingSeller';
  parent_type: 'notifications';
  data: TimeSeconds & {
    bid_id: string | number;
    buyer_id: string | number;
    buyer_name: string;
    hours_remaining: number;
    id: string | number;
    order_start_date: string;
    project_id: string | number;
    project_name: string;
    project_url: string;
    seller_id: string | number;
    seller_name: string;
    service_id: string | number;
    service_name: string;
  };
}

export interface WebsocketServiceProjectPostedSellerEvent
  extends BaseServerData {
  type: 'serviceProjectPostedSeller';
  parent_type: 'notifications';
  data: TimeSeconds & {
    bid_id: string | number;
    buyer_id: string | number;
    buyer_img_url: string;
    buyer_name: string;
    buyer_public_name?: string;
    currency_code: string;
    currency_sign: string;
    id: string | number;
    order_end_date: string;
    order_start_date: string;
    project_cost: string | number;
    // project_duration: ???;
    project_id: string | number;
    // project_milestones: ???;
    project_name: string;
    project_url: string;
    seller_id: string | number;
    seller_img_url: string;
    seller_name: string;
    seller_public_name: string;
    service_id: string | number;
    service_name: string;
    service_url: string;
  };
}

export interface WebsocketServiceStatusChangedEvent extends BaseServerData {
  type: 'serviceStatusChanged';
  parent_type: 'notifications';
  data: TimeSeconds & {
    id: string | number;
    // ???
  };
}

export interface WebsocketServiceRejectedEvent extends BaseServerData {
  type: 'serviceRejected';
  parent_type: 'notifications';
  data: WebsocketServiceData;
}

export interface WebsocketSignUpFreeTrialUpsellEvent extends BaseServerData {
  type: 'signUpFreeTrialUpsell';
  parent_type: 'notifications';
  data: TimeSeconds & {
    id: string | number;
    linkUrl: string;
    trialPackageName: string;
  };
}

export interface WebsocketUpsellCrowdsourceFindingEvent extends BaseServerData {
  type: 'upsellCrowdsourceFinding';
  parent_type: 'notifications';
  data: TimeSeconds & {
    id: string | number;
    project_name: string;
  };
}

export interface WebsocketXpContestEvent extends BaseServerData {
  type: 'xpContest';
  parent_type: 'notifications';
  data: WebsocketContestData & {
    currency: string;
    currencyCode: string;
    maxbudget: string | number;
  };
}

// Events that are there but we're ignoring.
// TODO: T38861 for bidHidden. It's an AB test.
export interface WebsocketDeprecatedEvent extends BaseServerData {
  type:
    | 'contactRequestSent'
    | 'contactRequestReceived'
    | 'bidHidden'
    | 'sitesBinCreated'
    | 'sitesHandOverMessageSent'
    | 'sitesListingStatusChanged'
    | 'sitesMessageSent'
    | 'sitesOfferCreated';
  parent_type: 'notifications';
  data: any;
}

// Events that are unknown/not defined yet
// Basically everything in event_notify.php that doesn't have a corresponding
// type in this file ends up here
export interface WebsocketUnknownEvent extends BaseServerData {
  type:
    | 'reviewActivate'
    | 'reviewPending'
    | 'rejected'
    | 'projectStatusChange'
    | 'negotiatedProjectStatusChange'
    | 'uploadFile'
    | 'contestEntryCreated'
    | 'contestCreated'
    | 'contestUpsellFeaturedUpgrade'
    | 'counterOffer'
    | 'editAwardedBidRequest'
    | 'editAwardedBidAccepted'
    | 'editAwardedBidDeclined'
    | 'pmb'
    | 'pmb_public'
    | 'contest_pcb'
    | 'contestreviewposted'
    | 'completeContestReview'
    | 'completeReview'
    | 'reviewposted'
    | 'reviewpostednew'
    | 'updateMilestone'
    | 'releasePartMilestone'
    | 'invoiceWithdrawn'
    | 'invoicePaid'
    | 'invoicePartialPaid'
    | 'invoiceRequestChange'
    | 'invoiceAcceptedPartial'
    | 'directTransferDone'
    | 'newDispute'
    | 'sendDisputeMessage'
    | 'acceptDisputeOffer'
    | 'makeDisputeOffer'
    | 'escalateDispute'
    | 'aborted'
    | 'emailChange'
    | 'buyFreeMarketService'
    | 'freeMarketServiceExpired'
    | 'freeMarketServiceIsExpired'
    | 'sellFreeMarketService'
    | 'adminForceVarifyPhone'
    | 'yahooMailAlert'
    | 'adminWarningUserBidRestriction'
    | 'downgradeToFreeMembershipNoti'
    | 'upgradeToNonFreeMembership'
    | 'bidsRunningOut'
    | 'removeUserFromDirectory'
    | 'banUserFromBidding'
    | 'winningDesignReadyForReview'
    | 'contestComplete'
    | 'contestCompleteSellEntryUpsell'
    | 'prizeDispersedEmployer'
    | 'prizeDispersedFreelancer'
    | 'contestLockedEmployer'
    | 'contestLockedFreelancer'
    | 'welcome'
    | 'invoiceFeedback'
    | 'postDraftProject'
    | 'projectHireMeExpired'
    | 'customAdminNotification'
    | 'inviteFriends'
    | 'generic'
    | 'contestEntryRated'
    | 'sponsor_bid'
    | 'kyc'
    | 'suggestionForFreelancerAfterReceiveReview'
    | 'upsellPlusTrial'
    | 'upsellFeatureUpgrade'
    | 'upsellAssistedUpgrade'
    | 'tombstoneNotiEmployer'
    | 'tombstoneNotiFreelancer'
    | 'tombstoneNoti'
    | 'communityPromoNoti'
    | 'expertsPromoNoti'
    | 'localJobsActivation'
    | 'firstEarning'
    | 'serviceProjectPostedBuyer' // possible union with 'serviceProjectPostedSeller'?
    | 'serviceProjectEndedBuyer'
    | 'serviceProjectEndedSeller'
    | 'activateFreelancer'
    | 'promoteContest'
    | 'sitesBidCreated'
    | 'remindUseBids'
    | 'secondBidPlusTrialUpsell'
    | 'pendingFunds'
    | 'examPassedUpsell'
    | 'examRetakingDiscount'
    | 'userSideAction'
    | 'userSideActionFeedback'
    | 'supportQueueEvent' // special - see event_notify.php QueueNotifSender::NAME
    | 'projectSharedWithYou'
    | 'prehireProjectCreated'
    | 'collaboratorAddedViaChat'
    | 'collaboratorsUpdatedRefreshChat'
    | 'balanceUpdate';
  parent_type: 'notifications';
  data: any;
}

type WebsocketNotificationEvent =
  | WebsocketArticleCommentEvent
  | WebsocketAwardEvent
  | WebsocketAwardBadgeEvent
  | WebsocketAwardCreditEvent
  | WebsocketAwardMilestoneReminderEvent
  | WebsocketAwardReminderEvent
  | WebsocketAwardXpEvent
  | WebsocketBidretractedEvent
  | WebsocketBidEvent
  | WebsocketCompletedEvent
  | WebsocketContestEvent
  | WebsocketContestAwardedToEmployerEvent
  | WebsocketContestAwardedToFreelancerEvent
  | WebsocketContestEntryEvent
  | WebsocketContestEntryBoughtToEmployerEvent
  | WebsocketContestEntryBoughtToFreelancerEvent
  | WebsocketContestExpiredEvent
  | WebsocketContestPCBNotificationEvent
  | WebsocketContestPCBNotificationFullViewEvent
  | WebsocketCreateMilestoneEvent
  | WebsocketAcceptedEvent
  | WebsocketRevokedEvent
  | WebsocketCancelMilestoneEvent
  | WebsocketDenyedEvent
  | WebsocketDraftContestEvent
  | WebsocketFailingProjectEvent
  | WebsocketHighLtvAnnualDiscountEvent
  | WebsocketHireMeEvent
  | WebsocketInviteToContestEvent
  | WebsocketInviteUserBidEvent
  | WebsocketInvoiceRequestedEvent
  | WebsocketLevelUpEvent
  | WebsocketLocalJobPostedEvent
  | WebsocketRecruiterProjectEvent
  | WebsocketNotifyFollowerEvent
  | WebsocketProjectEvent
  | WebsocketQuickHireProjectEvent
  | WebsocketReleaseMilestoneEvent
  | WebsocketRequestEndProjectEvent
  | WebsocketRequestMilestoneEvent
  | WebsocketRequestToReleaseEvent
  | WebsocketServiceApprovedEvent
  | WebsocketServiceProjectEndingSellerEvent
  | WebsocketServiceProjectPostedSellerEvent
  | WebsocketServiceStatusChangedEvent
  | WebsocketServiceRejectedEvent
  | WebsocketSignUpFreeTrialUpsellEvent
  | WebsocketUpsellCrowdsourceFindingEvent
  | WebsocketXpContestEvent
  | WebsocketDeprecatedEvent
  | WebsocketUnknownEvent;

// --- Online/offline events ---

export interface WebsocketOnlineOfflineSubscribeEvent {
  type: 'statusget';
  data: TimeSeconds & { [userId: string]: OnlineOfflineUserStatusValueApi };
  parent_type: 'onlineoffline';
}

export interface WebsocketOnlineOfflineStatusChangeEvent {
  type: 'statuschange';
  data: TimeSeconds & {
    from: OnlineOfflineUserStatusValueApi;
    timestamp: number;
    to: OnlineOfflineUserStatusValueApi;
    user: number;
  };
  parent_type: 'onlineoffline';
}

type WebsocketOnlineOfflineEvent =
  | WebsocketOnlineOfflineSubscribeEvent
  | WebsocketOnlineOfflineStatusChangeEvent;

// --- Messaging events ---

export interface WebsocketGroupChatEvent extends BaseServerData {
  type: 'groupchat';
  parent_type: 'notifications';
  data: TimeSeconds & {
    action: 'add' | 'remove';
    payload: { members: number[]; thread: number };
  };
}

export interface WebsocketVideoEvent {
  type:
    | 'video:request'
    | 'video:request:audio_only'
    | 'video:close'
    | 'video:accept'
    | 'video:reject';
  parent_type: 'messages';
  data: TimeSeconds & {
    session_id: string;
    thread_id: number;
    user_id: number;
  };
}

export interface WebsocketNewMessageEvent extends BaseServerData {
  type: 'private';
  parent_type: 'messages';
  data: TimeSeconds & {
    attachments: string[];
    client_message_id?: string;
    from_user: number;
    id: number;
    message: string;
    message_source: SourceTypeApi;
    notify_user: boolean;
    parent_id: number;
    rich_message?: RichMessageApi;
    thread_details: {
      context: {
        id?: number;
        type?: ContextTypeApi;
      };
      creator_id: number;
      members: number[];
      thread_type: ThreadTypeApi;
    };
    thread_id: number;
    time_created: number;
  };
}

export interface WebsocketTypingEvent {
  type: 'typing';
  parent_type: 'messages';
  data: TimeSeconds & {
    from_user: number;
    thread: number;
  };
}

export interface WebsocketUserReadEvent extends BaseServerData {
  type: 'user_read';
  parent_type: 'messages';
  data: TimeSeconds & {
    id: string | number;
    thread_ids: number[];
    user_id: number;
  };
}

export interface WebsocketUserAttachEvent extends BaseServerData {
  type: 'attach';
  parent_type: 'messages';
  data: TimeSeconds & {
    filenames: string[];
    from_user: number;
    message_id: number;
    thread_id: number;
  };
  id: string;
}

export interface WebsocketThreadWritePrivacyUpdateEvent {
  type: 'write_privacy_update';
  parent_type: 'messages';
  data: {
    thread_ids: number[];
    write_privacy: PrivacyLevelApi;
  };
}

type WebsocketMessagingEvent =
  | WebsocketGroupChatEvent
  | WebsocketNewMessageEvent
  | WebsocketThreadWritePrivacyUpdateEvent
  | WebsocketTypingEvent
  | WebsocketUserReadEvent
  | WebsocketUserAttachEvent
  | WebsocketVideoEvent;

export type WebsocketEvent =
  | WebsocketNotificationEvent
  | WebsocketOnlineOfflineEvent
  | WebsocketMessagingEvent;

export function isOnlineOfflineEvent(
  event: WebsocketEvent,
): event is WebsocketOnlineOfflineEvent {
  return event.parent_type === 'onlineoffline';
}

export function isWebsocketMessage(
  event: WebsocketServerEvent,
): event is WebsocketMessage {
  return event.channel === 'user';
}

type BaseMessage = BaseServerData & {
  channel: 'user';
  body: {
    aggregated: null;
    display: boolean;
    id: string;
    is_important: boolean;
    is_notification: boolean;
    is_project: boolean;
    message: string;
    news_feed: boolean;
    no_persist: boolean;
    popup: boolean;
    targets: any[];
    timestamp: number;
  };
};

export type WebsocketMessage = BaseMessage & {
  body: WebsocketEvent;
};



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/core/websocket/server-message-event.model.ts